import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LogIn, LogOut, Sun, Moon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LanguageSelect from "@/components/LanguageSelect";
import { useTheme } from "@/context/ThemeContext";
import { logoutSession } from "@/lib/api";

export default function Navbar() {
  const { user, setUser } = useAuth();
  const { lang, t } = useLang();
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await logoutSession();
    } catch {}
    setUser(null);
    toast.success(t("dl.logoutOk"));
  };

  const homePrefix = lang === "en" ? "/en" : "";
  const pagePrefix = lang === "en" ? "/en" : "";
  const mainLinks = [
    { key: "tiktok", href: `${homePrefix}#downloader` },
    { key: "guides", href: `${pagePrefix}/guides` },
    { key: "faq", href: `${homePrefix}#faq` },
    { key: "about", href: `${pagePrefix}/about` },
  ];

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 glass border-b transition-[box-shadow,border-color] duration-300 ${
        scrolled ? "border-neon/25 shadow-[0_8px_30px_rgba(0,0,0,0.35)]" : "border-white/10"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-12 h-16 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-2 group shrink-0" data-testid="nav-logo">
          <img
            src="/logo192.webp"
            alt="SS Downloader"
            data-testid="nav-logo-icon"
            className="h-9 w-9 rounded-xl transition-transform duration-300 group-hover:rotate-12"
          />
          <span className="font-display text-xl sm:text-2xl font-black leading-none pt-0.5 whitespace-nowrap">
            <span className="neon-text-gradient">SS</span>{" "}
            <span className="text-ice hidden xs:inline sm:inline">Downloader</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-6 text-sm text-muted-foreground">
          {mainLinks.map((p) => (
            <a
              key={p.key}
              href={p.href}
              data-testid={`nav-link-${p.key}`}
              className="hover:text-neon transition-colors duration-200 font-bold"
            >
              {t(`nav.${p.key}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={toggleTheme}
            data-testid="nav-theme-toggle"
            aria-label={t("nav.theme")}
            className="rounded-full border border-white/20 p-2 text-muted-foreground transition-colors duration-200 hover:border-neon hover:text-neon active:scale-95"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <LanguageSelect compact />
          {user ? (
            <div className="flex items-center gap-2" data-testid="nav-user-menu">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name || "user"}
                  className="h-8 w-8 rounded-full border border-neon/60"
                  data-testid="nav-user-avatar"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="grid place-items-center h-8 w-8 rounded-full bg-neon text-void text-xs font-bold">
                  {(user.name || "?").charAt(0)}
                </span>
              )}
              <button
                onClick={handleLogout}
                data-testid="nav-logout-button"
                className="rounded-full border border-white/20 p-2 text-muted-foreground transition-colors duration-200 hover:border-neon hover:text-neon active:scale-95"
                aria-label={t("nav.logout")}
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogin}
              data-testid="nav-google-login-button"
              className="hidden sm:flex rounded-full bg-neon px-5 py-2 text-sm font-bold text-void items-center gap-2 neon-glow transition-transform duration-200 hover:scale-105 active:scale-95"
            >
              <LogIn className="h-4 w-4" />
              {t("nav.login")}
            </button>
          )}
        </div>
      </div>
    </motion.header>
  );
}
